// E-mail address helpers for the sign-in form: complete the domain as the person types
// ("name@g" → gmail.com) and catch typos ("name@gemal.com" → gmail.com).

/** Most-used first: suggestions keep this order. */
export const EMAIL_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "yahoo.com",
  "live.com",
  "outlook.sa",
  "hotmail.co.uk",
  "msn.com",
  "me.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
];

/** Optimal-string-alignment distance: insertions, deletions, substitutions and swapped neighbours cost 1. */
function distance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array<number>(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0]![j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1);
    }
  }
  return d[a.length]![b.length]!;
}

export type EmailSuggestion = { email: string; kind: "complete" | "typo" };

/**
 * Suggestions for what has been typed so far.
 * - "name@" or "name@ho" → completions (kind "complete"), most-used domains first.
 * - "name@gemal.com", "name@gmail.con", "name@gmial" → the closest known domain (kind "typo").
 * - A known domain, or one that is clearly something else (a company address) → nothing.
 */
export function suggestEmails(input: string, max = 3): EmailSuggestion[] {
  const value = input.trim().toLowerCase();
  const at = value.indexOf("@");
  if (at < 1 || value.indexOf("@", at + 1) !== -1) return [];
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (EMAIL_DOMAINS.includes(domain)) return [];

  const completions = EMAIL_DOMAINS.filter((d) => d.startsWith(domain) && d !== domain);
  if (completions.length) return completions.slice(0, max).map((d) => ({ email: `${local}@${d}`, kind: "complete" }));
  if (domain.length < 3) return [];

  // Compare the whole domain ("gmail.con") and, without a dot, just the name ("gemal" vs "gmail").
  const name = domain.split(".")[0]!;
  let best: { d: string; score: number } | null = null;
  for (const d of EMAIL_DOMAINS) {
    const score = Math.min(distance(domain, d), domain.includes(".") ? Infinity : distance(name, d.split(".")[0]!));
    if (!best || score < best.score) best = { d, score };
  }
  const allowed = domain.length <= 4 ? 1 : 2; // short input is ambiguous; stay conservative
  if (!best || best.score === 0 || best.score > allowed) return [];
  return [{ email: `${local}@${best.d}`, kind: "typo" }];
}
